









import Enumerable from "linq";
import { Component, Mixins } from "vue-property-decorator";
import { EditingDocument } from "../..";
import { DomainRegistry } from "../../../../../domain";
import { MenuPane } from "../../../../panes";
import { ModelEditableMixin, ModalPresentableMixin } from "../../../../system/mixins";
import { Document } from "../../../../../domain/document";
import { EditRuleSetApplicator, FontSizeInitializer, HisInformationApplicator, MoneyFormatInitializer } from "../../helpers";
import { FormatStatus } from "../../../../../domain/format";
import { SignItem, StampItem } from "../../../../../domain/document/items";

@Component({
  components: {
    MenuPane,
  }
})
export default class FormatMenu extends Mixins(ModelEditableMixin, ModalPresentableMixin) {
  data(): any {
    return {
      isLoading: false,
    };
  }
  isLoading: boolean;

  get isVisible(): boolean {
    return this.$configuration.behavior.document.editor.formatUpdate.isEnable;
  }

  get editingDocument(): EditingDocument {
    return this.editingSource;
  }

  get isFormatReplaceable(): boolean {
    // TODO: 最新のフォーマットだったらそもそもボタンをdisableにできたら嬉しいが・・
    return this.isRegisteredDocument || this.isCopiedDocument || this.$configuration.debug.forceDocumentFormatUpdatable;
  }

  get isRegisteredDocument(): boolean {
    return (this.editingDocument.source.documentId != null);
  }

  get isCopiedDocument(): boolean {
    return (this.editingDocument.attributes.copiedDocumentId != null);
  }

  get formatNameWording(): string {
    return this.$wording.format.name;
  }

  async replaceFormat(): Promise<void> {
    try {
      this.beginModal();
      if (this.isSignedDocument()) {
        this.$warn(
          "更新中止",
          "サイン済みの文書のため" + this.formatNameWording + "の更新を中止しました。",
        );
        return;
      }
      if (this.isStampedDocument()) {
        this.$warn(
          "更新中止",
          "印影が押されている文書のため" + this.formatNameWording + "の更新を中止しました。",
        );
        return;
      }

      const isConfirmed = await this.confirmToReplaceFormat();
      if (!isConfirmed) { return; }

      this.isLoading = true;
      try {
        const newEditingDocument = await this.createMergedDocument();
        if (!newEditingDocument) { return; }
        this.input(newEditingDocument);
        this.$success(
          "更新成功",
          this.formatNameWording + "を更新しました。",
        );
      } catch (e) {
        this.$error(
          this.formatNameWording + "更新失敗",
          "最新" + this.formatNameWording + "への更新処理中にエラーが発生しました。",
          e,
        );
      } finally {
        this.isLoading = false;
      }
    } finally {
      this.endModal();
    }
  }

  private isSignedDocument(): boolean {
    return Enumerable.from(this.editingDocument.itemSets)
      .selectMany(s => s.items)
      .any(i => i instanceof SignItem && i.hasImage);
  }

  private isStampedDocument(): boolean {
    // 移行処理を修正前に移行したデータではisStamped=falseなのに押印があるデータがあり得る
    return Enumerable.from(this.editingDocument.itemSets)
      .selectMany(s => s.items)
      .any(i => i instanceof StampItem && (i.isStamped || i.hasImage));
  }

  private async createMergedDocument(): Promise<EditingDocument> {
    this.$logger.info("最新" + this.formatNameWording + "への更新処理を開始します。");

    const format = await this.getLatestFormatStatus();
    if (!format) {
      // DBを直接編集しない限りは絶対にフォーマットはあるはず
      this.$warn("更新不可", "最新の" + this.formatNameWording + "が見つかりませんでした。");
      return null;
    }

    if (!this.$configuration.debug.forceDocumentFormatUpdatable && this.editingDocument.attributes.format.id === format.id) {
      this.$warn("更新不要", "最新の" + this.formatNameWording + "なので更新は不要です。");
      return null;
    }

    if (format.deleted || format.expired) {
      const message = format.expired
        ? this.formatNameWording + "の有効期限が切れています。"
        : this.formatNameWording + "が削除されています。";
      this.$warn("更新不可", message);
      return null;
    }

    const document = await this.getNewEssentialDocument();
    if (!document) { return null; }

    // 文書の付帯情報をコピー
    document.forceOverwrite(this.editingDocument.source);
    document.freezeItems();

    // 文書のオブジェクトの内容をコピー
    const newEditingDocument = new EditingDocument(document);
    // 制約の注入
    const editRuleSetApplicator = new EditRuleSetApplicator(newEditingDocument, this.$store.getters.user);
    await editRuleSetApplicator.apply();
    await newEditingDocument.initialize();
    const isExternalScriptSuccess = await newEditingDocument.merge(this.editingDocument.source, true, true);
    if (!isExternalScriptSuccess) { throw new Error("外部スクリプトの処理中にエラーが発生しました。"); }

    // 最終更新日・最終更新者を更新（他のHIS項目も対応必要になるかもだが、ワークフローの挙動も含めて慎重に検討が必要）
    const applicator = new HisInformationApplicator(newEditingDocument);
    applicator.applyByFormatUpdate(newEditingDocument.hisInformation, this.$store.getters.user);

    const moneyFormatInitializer = new MoneyFormatInitializer(newEditingDocument);
    moneyFormatInitializer.initialize();
    const fontSizeInitializer = new FontSizeInitializer(newEditingDocument);
    fontSizeInitializer.initialize();

    const isStackedExternalScriptSuccess = await newEditingDocument.executeStackedScriptsAsyncWithResult();
    if (!isStackedExternalScriptSuccess) { throw new Error("外部スクリプトの処理中にエラーが発生しました。"); }

    newEditingDocument.currentSectionIndex = this.editingDocument.currentSectionIndex;
    newEditingDocument.restrictions = this.editingDocument.restrictions;
    newEditingDocument.scale = this.editingDocument.scale;
    newEditingDocument.shouldDrawEditStyle = this.editingDocument.shouldDrawEditStyle;
    newEditingDocument.shouldInputFreeAll = this.editingDocument.shouldInputFreeAll;
    newEditingDocument.shouldDrawTransparentAll = this.editingDocument.shouldDrawTransparentAll;
    // alerts・shouldMarkAlertsはフォーマット内の警告設定が変更されている可能性があるのでコピーはしない

    this.$logger.info("最新" + this.formatNameWording + "への更新処理が完了しました。");
    return newEditingDocument;
  }

  private async confirmToReplaceFormat(): Promise<boolean> {
    try {
      const index = await this.$commonDialogs.confirmation.confirmUpdateCancel(
        this.formatNameWording + "の更新",
        [this.formatNameWording + "を更新してもよろしいですか？"]
      );
      return (index === 0);
    } catch {
      // キャンセルなど
    }
    return false;
  }

  private async getNewEssentialDocument(): Promise<Document> {
    try {
      const repository = DomainRegistry.document.getDocumentRepository();
      return repository.getNewEssentialDocument(this.editingDocument.attributes.patient.id, this.editingDocument.attributes.format.originalId);
    } catch (e) {
      this.$error(
        "最新" + this.formatNameWording + "取得失敗",
        "最新" + this.formatNameWording + "を取得できませんでした。",
        e,
      );
    }
    return null;
  }

  private async getLatestFormatStatus(): Promise<FormatStatus> {
    try {
      const repository = DomainRegistry.format.getFormatRepository();
      return repository.getFormatStatus(this.editingDocument.attributes.format.originalId);
    } catch (e) {
      this.$logger.warn(e);
    }
    return null;
  }
}
